import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Plus, Printer, X } from 'lucide-react';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  Field,
  Input,
  Select,
  Spinner,
  Textarea,
  type Tone,
} from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { Section, Table, td, tdR, th, thR } from '../components/reports/blocks';
import { istanbulToday } from '../components/reports/period';
import { api, ApiError, IdempotencyKey } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { bpsLabel, minorToInput, toMinor, useMoney } from '../lib/money';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { Warehouse } from '../lib/types';
import type { PurchaseOrderDetail, PurchaseOrderList, SupplierList } from '../lib/types-faz8';
import { ReceiveDialog } from './goods-receipt';
import { percentToBps } from './pricing';
import { ProductVariantPicker } from './stock';

/**
 * Satın alma (F8-06, ADR-807): sipariş listesi, yeni talep, ayrıntı (sipariş ver, iptal, kapat, mal kabul, yazdır).
 * Rehber: docs/faz-8/PURCHASING_TR.md.
 */

const STATUS_TONE: Record<string, Tone> = {
  requested: 'slate',
  ordered: 'pine',
  partially_received: 'amber',
  received: 'moss',
  closed: 'neutral',
  cancelled: 'neutral',
};
const FILTERS = [
  'open',
  'all',
  'requested',
  'ordered',
  'partially_received',
  'received',
  'closed',
  'cancelled',
] as const;
const CURRENCIES = ['TRY', 'USD', 'EUR', 'GBP'] as const;

const plusDays = (day: string, n: number) => {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

export function PoStatusBadge({ status }: { status: string }) {
  const t = useT();
  return <Badge tone={STATUS_TONE[status] ?? 'neutral'}>{t(`admin.purchasing.statuses.${status}`)}</Badge>;
}

export function PurchaseOrdersPage() {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const [status, setStatus] = useState<(typeof FILTERS)[number]>('open');
  const q = useQuery({
    queryKey: ['purchase-orders', status],
    queryFn: () =>
      api.get<PurchaseOrderList>(`/v1/admin/purchase-orders${status === 'all' ? '' : `?status=${status}`}`),
    placeholderData: keepPreviousData,
  });
  return (
    <PageFrame
      helpKey="page.purchase_orders"
      actions={
        can('purchasing.manage') && (
          <Link
            to="/satin-alma/yeni"
            className="inline-flex h-10 items-center gap-2 rounded-md bg-pine px-4 text-sm font-medium text-white hover:bg-pine/90"
          >
            <Plus className="h-4 w-4" aria-hidden />
            {t('admin.purchasing.newOrder')}
          </Link>
        )
      }
    >
      <Field label={t('admin.purchasing.orderCols.status')} className="max-w-xs">
        {(p) => (
          <Select {...p} value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
            {FILTERS.map((f) => (
              <option key={f} value={f}>
                {f === 'open' || f === 'all'
                  ? t(`admin.purchasing.filters.${f}`)
                  : t(`admin.purchasing.statuses.${f}`)}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <div className="mt-4">
        {q.isError ? (
          <p role="alert" className="text-sm text-rose">
            {q.error instanceof ApiError ? q.error.title : String(q.error)}
          </p>
        ) : !q.data ? (
          <Spinner label={t('common.loading')} />
        ) : !q.data.length ? (
          <EmptyState
            title={t('admin.purchasing.noOrders')}
            description={t('admin.purchasing.noOrdersLead')}
          />
        ) : (
          <Table
            caption={t('admin.purchasing.navOrders')}
            head={
              <tr>
                {(
                  ['number', 'supplier', 'warehouse', 'expected', 'status', 'progress', 'total'] as const
                ).map((k) => (
                  <th key={k} scope="col" className={k === 'progress' || k === 'total' ? thR : th}>
                    {t(`admin.purchasing.orderCols.${k}`)}
                  </th>
                ))}
              </tr>
            }
          >
            {q.data.map((o) => (
              <tr key={o.id} className="border-t border-line">
                <th scope="row" className={`${td} text-start font-normal`}>
                  <Link
                    to="/satin-alma/$id"
                    params={{ id: o.id }}
                    className="font-medium text-pine hover:underline"
                  >
                    {o.number}
                  </Link>
                </th>
                <td className={td}>{o.supplierName}</td>
                <td className={td}>{o.warehouseName}</td>
                <td className={`${td} whitespace-nowrap`}>
                  {o.expectedDate ? formatDate(o.expectedDate, false) : '—'}
                </td>
                <td className={td}>
                  <PoStatusBadge status={o.status} />
                </td>
                <td className={tdR}>
                  {t('admin.purchasing.progress', {
                    received: o.receivedQuantity,
                    ordered: o.orderedQuantity,
                  })}
                </td>
                <td className={tdR}>{money(o.totalMinor, o.currency)}</td>
              </tr>
            ))}
          </Table>
        )}
      </div>
    </PageFrame>
  );
}

// ------------------------------------------------------------------------------------------ Talep formu

interface FormLine {
  variantId: string;
  label: string;
  quantity: string;
  unitCost: string;
  tax: string;
}
interface FormValue {
  supplierId: string;
  warehouseId: string;
  currency: string;
  expectedDate: string;
  note: string;
  lines: FormLine[];
}

function parseLines(lines: FormLine[], currency: string) {
  return lines.map((l) => {
    const quantity = /^\d+$/.test(l.quantity.trim()) ? Number(l.quantity) : NaN;
    const unitCostMinor = toMinor(l.unitCost, currency);
    const taxRateBps = l.tax.trim() ? percentToBps(l.tax) : undefined;
    return { ...l, quantityN: quantity, unitCostMinor, taxRateBps };
  });
}

function PoForm({
  initial,
  lockHeader,
  submitLabel,
  pending,
  error,
  onSubmit,
}: {
  initial: FormValue;
  lockHeader?: boolean;
  submitLabel: string;
  pending: boolean;
  error: unknown;
  onSubmit: (v: FormValue, body: Record<string, unknown>) => void;
}) {
  const t = useT();
  const money = useMoney();
  const [v, setV] = useState(initial);
  const [productQ, setProductQ] = useState('');
  const suppliers = useQuery({
    queryKey: ['suppliers', 'active'],
    queryFn: () => api.get<SupplierList>('/v1/admin/suppliers?status=active'),
  });
  const warehouses = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => api.get<Warehouse[]>('/v1/admin/warehouses'),
  });
  const products = useQuery({
    queryKey: ['products', 'pick', productQ],
    queryFn: () =>
      api.get<{ items: Array<{ id: string; title: string }> }>(
        `/v1/admin/products?q=${encodeURIComponent(productQ)}&limit=8`,
      ),
    enabled: productQ.trim().length >= 2,
  });
  const parsed = parseLines(v.lines, v.currency);
  const valid =
    v.supplierId &&
    v.warehouseId &&
    parsed.length > 0 &&
    parsed.every(
      (l) =>
        Number.isInteger(l.quantityN) &&
        l.quantityN >= 1 &&
        l.unitCostMinor !== null &&
        l.taxRateBps !== null,
    );
  const subtotal = parsed.reduce(
    (a, l) => a + (Number.isInteger(l.quantityN) && l.unitCostMinor ? l.quantityN * l.unitCostMinor : 0),
    0,
  );
  const setLine = (i: number, k: keyof FormLine, val: string) =>
    setV((x) => ({ ...x, lines: x.lines.map((l, j) => (j === i ? { ...l, [k]: val } : l)) }));
  const fe = (k: string) => fieldError(error, k);
  const submit = () =>
    onSubmit(v, {
      ...(lockHeader ? {} : { supplierId: v.supplierId, warehouseId: v.warehouseId, currency: v.currency }),
      ...(lockHeader ? { warehouseId: v.warehouseId, currency: v.currency } : {}),
      expectedDate: v.expectedDate || null,
      note: v.note.trim() || null,
      lines: parsed.map((l) => ({
        variantId: l.variantId,
        quantity: l.quantityN,
        unitCostMinor: l.unitCostMinor!,
        ...(l.taxRateBps !== undefined ? { taxRateBps: l.taxRateBps } : {}),
      })),
    });
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t('admin.purchasing.supplier')} required error={fe('supplierId')}>
          {(p) => (
            <Select
              {...p}
              value={v.supplierId}
              disabled={lockHeader}
              onChange={(e) => {
                const s = suppliers.data?.find((x) => x.id === e.target.value);
                // Beklenen tarih boşsa tedarikçinin teslim süresinden önerilir.
                setV((x) => ({
                  ...x,
                  supplierId: e.target.value,
                  currency: s?.currency ?? x.currency,
                  expectedDate:
                    x.expectedDate ||
                    (s?.leadTimeDays !== null && s?.leadTimeDays !== undefined
                      ? plusDays(istanbulToday(), s.leadTimeDays)
                      : ''),
                }));
              }}
            >
              <option value="">{t('admin.purchasing.pickSupplier')}</option>
              {(suppliers.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.code})
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('admin.purchasing.warehouse')} required error={fe('warehouseId')}>
          {(p) => (
            <Select
              {...p}
              value={v.warehouseId}
              onChange={(e) => setV((x) => ({ ...x, warehouseId: e.target.value }))}
            >
              <option value="">{t('admin.purchasing.pickWarehouse')}</option>
              {(warehouses.data ?? [])
                .filter((w) => w.status === 'active')
                .map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
            </Select>
          )}
        </Field>
        <Field label={t('admin.purchasing.fields.currency')} required>
          {(p) => (
            <Select
              {...p}
              value={v.currency}
              onChange={(e) => setV((x) => ({ ...x, currency: e.target.value }))}
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field
          label={t('admin.purchasing.expectedDate')}
          optionalLabel={t('common.optional')}
          help={<HelpTip k="purchasing.lead_time" />}
          error={fe('expectedDate')}
        >
          {(p) => (
            <Input
              {...p}
              type="date"
              value={v.expectedDate}
              onChange={(e) => setV((x) => ({ ...x, expectedDate: e.target.value }))}
            />
          )}
        </Field>
        <div className="sm:col-span-2">
          <Field label={t('admin.purchasing.note')} optionalLabel={t('common.optional')}>
            {(p) => (
              <Textarea
                {...p}
                rows={2}
                value={v.note}
                onChange={(e) => setV((x) => ({ ...x, note: e.target.value }))}
              />
            )}
          </Field>
        </div>
      </div>

      <Section title={t('admin.purchasing.linesTitle')} help={<HelpTip k="purchasing.unit_cost" />}>
        <Field label={t('admin.purchasing.addProduct')}>
          {(p) => (
            <Input {...p} type="search" value={productQ} onChange={(e) => setProductQ(e.target.value)} />
          )}
        </Field>
        {products.data && productQ.trim().length >= 2 && (
          <ProductVariantPicker
            productIds={products.data.items.map((i) => ({ id: i.id, title: i.title }))}
            onPick={(variantId, label) => {
              setV((x) =>
                x.lines.some((l) => l.variantId === variantId)
                  ? x
                  : { ...x, lines: [...x.lines, { variantId, label, quantity: '1', unitCost: '', tax: '' }] },
              );
              setProductQ('');
            }}
          />
        )}
        {fe('lines') && (
          <p role="alert" className="mt-2 text-sm text-rose">
            {fe('lines')}
          </p>
        )}
        {v.lines.length > 0 && (
          <ul className="mt-4 space-y-3">
            {parsed.map((l, i) => (
              <li key={l.variantId} className="rounded-md border border-line p-3">
                <div className="mb-2 flex items-start justify-between gap-2">
                  <span className="text-sm font-medium text-ink">{l.label}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`${t('admin.purchasing.removeLine')}: ${l.label}`}
                    onClick={() => setV((x) => ({ ...x, lines: x.lines.filter((_, j) => j !== i) }))}
                  >
                    <X className="h-4 w-4" aria-hidden />
                  </Button>
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  <Field
                    label={t('admin.purchasing.lineCols.quantity')}
                    required
                    error={
                      l.quantity.trim() && !(Number.isInteger(l.quantityN) && l.quantityN >= 1)
                        ? t('admin.purchasing.quantityInvalid')
                        : fe(`lines.${i}.quantity`)
                    }
                  >
                    {(p) => (
                      <Input
                        {...p}
                        inputMode="numeric"
                        value={l.quantity}
                        onChange={(e) => setLine(i, 'quantity', e.target.value)}
                      />
                    )}
                  </Field>
                  <Field
                    label={`${t('admin.purchasing.lineCols.unitCost')} (${v.currency})`}
                    required
                    error={
                      l.unitCost.trim() && l.unitCostMinor === null
                        ? t('admin.purchasing.costInvalid')
                        : fe(`lines.${i}.unitCostMinor`)
                    }
                  >
                    {(p) => (
                      <Input
                        {...p}
                        inputMode="decimal"
                        value={l.unitCost}
                        onChange={(e) => setLine(i, 'unitCost', e.target.value)}
                      />
                    )}
                  </Field>
                  <Field
                    label={t('admin.purchasing.lineCols.tax')}
                    optionalLabel={t('admin.purchasing.taxDefault')}
                    error={
                      l.tax.trim() && l.taxRateBps === null ? t('admin.purchasing.costInvalid') : undefined
                    }
                  >
                    {(p) => (
                      <Input
                        {...p}
                        inputMode="decimal"
                        value={l.tax}
                        onChange={(e) => setLine(i, 'tax', e.target.value)}
                      />
                    )}
                  </Field>
                </div>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-3 text-sm text-ink-2" role="status">
          {t('admin.purchasing.totals.subtotal')}: {money(subtotal, v.currency)}
        </p>
      </Section>
      <Button variant="primary" loading={pending} disabled={!valid} onClick={submit}>
        {submitLabel}
      </Button>
    </div>
  );
}

export function PurchaseOrderNewPage() {
  const t = useT();
  const navigate = useNavigate();
  const [idem] = useState(() => new IdempotencyKey());
  const create = useApiMutation(
    (body: Record<string, unknown>) =>
      api.post<{ id: string }>('/v1/admin/purchase-orders', body, { idempotencyKey: idem }),
    {
      invalidate: [['purchase-orders']],
      success: t('admin.purchasing.orderCreated'),
      onSuccess: (r) => void navigate({ to: '/satin-alma/$id', params: { id: r.id } }),
      silentFieldErrors: true,
    },
  );
  return (
    <PageFrame helpKey="page.purchase_order_new">
      <PoForm
        initial={{ supplierId: '', warehouseId: '', currency: 'TRY', expectedDate: '', note: '', lines: [] }}
        submitLabel={t('admin.purchasing.createOrder')}
        pending={create.isPending}
        error={create.error}
        onSubmit={(_, body) => create.mutate(body)}
      />
    </PageFrame>
  );
}

// ------------------------------------------------------------------------------------------ Ayrıntı

export function PurchaseOrderDetailPage() {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const { id } = useParams({ from: '/app/satin-alma/$id' });
  const [editing, setEditing] = useState(false);
  const [receiving, setReceiving] = useState(false);
  const [dating, setDating] = useState(false);
  const q = useQuery({
    queryKey: ['purchase-order', id],
    queryFn: () => api.get<PurchaseOrderDetail>(`/v1/admin/purchase-orders/${id}`),
  });
  const act = (action: 'order' | 'cancel' | 'close', success: string) =>
    api
      .post(`/v1/admin/purchase-orders/${id}/${action}`, {}, { ifMatch: q.data?.version })
      .then(() => success);
  const transition = useApiMutation(
    (v: { action: 'order' | 'cancel' | 'close'; success: string }) => act(v.action, v.success),
    { invalidate: [['purchase-order', id], ['purchase-orders'], ['receivable']], success: (m) => m },
  );
  if (q.isError)
    return (
      <PageFrame helpKey="page.purchase_order">
        <p role="alert" className="text-sm text-rose">
          {q.error instanceof ApiError ? q.error.title : String(q.error)}
        </p>
      </PageFrame>
    );
  if (!q.data)
    return (
      <PageFrame helpKey="page.purchase_order">
        <Spinner label={t('common.loading')} />
      </PageFrame>
    );
  const po = q.data;
  const manage = can('purchasing.manage');
  const receivable =
    (po.status === 'ordered' || po.status === 'partially_received') && can('purchasing.receive');
  const info: Array<[string, ReactNode]> = [
    [
      t('admin.purchasing.supplier'),
      <Link
        key="s"
        to="/tedarikciler/$id"
        params={{ id: po.supplierId }}
        className="text-pine hover:underline"
      >
        {po.supplierName}
      </Link>,
    ],
    [t('admin.purchasing.warehouse'), po.warehouseName],
    [t('admin.purchasing.fields.currency'), po.currency],
    [
      t('admin.purchasing.expectedDate'),
      po.expectedDate ? formatDate(po.expectedDate, false) : t('admin.purchasing.noExpected'),
    ],
    [t('admin.purchasing.requestedOn'), formatDate(po.createdAt, false)],
    [t('admin.purchasing.orderedOn'), po.orderedAt ? formatDate(po.orderedAt, false) : '—'],
    [t('admin.purchasing.note'), po.note ?? '—'],
  ];
  return (
    <PageFrame
      helpKey="page.purchase_order"
      title={`${t('admin.purchasing.navOrders')} ${po.number}`}
      printable
      actions={
        <span className="inline-flex flex-wrap items-center gap-2">
          {po.status === 'requested' && manage && (
            <>
              <Button onClick={() => setEditing(true)}>{t('admin.purchasing.editLines')}</Button>
              <ConfirmButton
                variant="primary"
                size="md"
                message={t('admin.purchasing.placeConfirm')}
                onConfirm={() =>
                  transition.mutateAsync({ action: 'order', success: t('admin.purchasing.placed') })
                }
              >
                {t('admin.purchasing.placeOrder')}
              </ConfirmButton>
              <HelpTip k="purchasing.place_order" />
            </>
          )}
          {receivable && (
            <Button variant="primary" onClick={() => setReceiving(true)}>
              {t('admin.purchasing.receive')}
            </Button>
          )}
          {(po.status === 'ordered' || po.status === 'partially_received') && manage && (
            <Button onClick={() => setDating(true)}>{t('admin.purchasing.changeDate')}</Button>
          )}
          {po.status === 'partially_received' && manage && (
            <ConfirmButton
              variant="secondary"
              size="md"
              message={t('admin.purchasing.closeConfirm')}
              onConfirm={() =>
                transition.mutateAsync({ action: 'close', success: t('admin.purchasing.closed') })
              }
            >
              {t('admin.purchasing.closeOrder')}
            </ConfirmButton>
          )}
          {(po.status === 'requested' || po.status === 'ordered') && manage && (
            <ConfirmButton
              size="md"
              message={t('admin.purchasing.cancelConfirm')}
              onConfirm={() =>
                transition.mutateAsync({ action: 'cancel', success: t('admin.purchasing.cancelled') })
              }
            >
              {t('admin.purchasing.cancelOrder')}
            </ConfirmButton>
          )}
          <Button onClick={() => window.print()}>
            <Printer className="h-4 w-4" aria-hidden />
            {t('admin.purchasing.print')}
          </Button>
        </span>
      }
    >
      <h2 className="mb-3 hidden text-xl font-semibold print:block">
        {t('admin.purchasing.navOrders')} {po.number}
      </h2>
      <PoStatusBadge status={po.status} />
      <dl className="mt-4 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        {info.map(([k, v]) => (
          <div key={k} className="flex gap-2">
            <dt className="min-w-36 text-ink-3">{k}</dt>
            <dd className="text-ink">{v}</dd>
          </div>
        ))}
      </dl>

      <div className="mt-8">
        <Section title={t('admin.purchasing.linesTitle')}>
          <Table
            caption={t('admin.purchasing.linesTitle')}
            head={
              <tr>
                {(['product', 'quantity', 'received', 'remaining', 'unitCost', 'tax', 'total'] as const).map(
                  (k) => (
                    <th key={k} scope="col" className={k === 'product' ? th : thR}>
                      {t(`admin.purchasing.lineCols.${k}`)}
                    </th>
                  ),
                )}
              </tr>
            }
          >
            {po.lines.map((l) => (
              <tr key={l.id} className="border-t border-line">
                <th scope="row" className={`${td} text-start font-normal`}>
                  {l.title}
                  <span className="block text-xs text-ink-3">{l.sku}</span>
                </th>
                <td className={tdR}>{l.quantity}</td>
                <td className={tdR}>{l.receivedQuantity}</td>
                <td className={tdR}>{l.remaining}</td>
                <td className={tdR}>{money(l.unitCostMinor, po.currency)}</td>
                <td className={tdR}>{bpsLabel(l.taxRateBps)}</td>
                <td className={tdR}>{money(l.quantity * l.unitCostMinor, po.currency)}</td>
              </tr>
            ))}
          </Table>
          <dl className="ms-auto mt-3 max-w-xs space-y-1 text-sm">
            {(['subtotal', 'tax', 'total'] as const).map((k) => (
              <div key={k} className={`flex justify-between gap-4 ${k === 'total' ? 'font-semibold' : ''}`}>
                <dt>{t(`admin.purchasing.totals.${k}`)}</dt>
                <dd className="tabular">{money(po[`${k}Minor`], po.currency)}</dd>
              </div>
            ))}
          </dl>
        </Section>
      </div>

      <div className="mt-8 print:hidden">
        <Section title={t('admin.purchasing.receiptsTitle')}>
          {!po.receipts.length ? (
            <EmptyState title={t('admin.purchasing.noReceipts')} />
          ) : (
            <ul className="space-y-3">
              {po.receipts.map((r) => (
                <li key={r.id} className="rounded-md border border-line p-3 text-sm">
                  <p className="font-medium text-ink">
                    {t('admin.purchasing.receiptLine', {
                      number: r.number,
                      date: formatDate(r.receivedOn, false),
                      count: r.lines.length,
                    })}
                    {r.invoiceNo && <span className="ms-2 font-normal text-ink-3">{r.invoiceNo}</span>}
                  </p>
                  <ul className="mt-1 text-ink-2">
                    {r.lines.map((x) => {
                      const line = po.lines.find((l) => l.id === x.purchaseOrderLineId);
                      return (
                        <li key={x.id}>
                          {line?.sku ?? ''} × {x.quantity}
                          {x.lotNo && ` · ${t('admin.purchasing.lot', { lot: x.lotNo })}`}
                          {x.expiryDate &&
                            ` · ${t('admin.purchasing.expiry', { date: formatDate(x.expiryDate, false) })}`}
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
      {editing && <EditDialog po={po} onClose={() => setEditing(false)} />}
      {dating && <DateDialog po={po} onClose={() => setDating(false)} />}
      {receiving && (
        <ReceiveDialog
          order={{
            id: po.id,
            number: po.number,
            currency: po.currency,
            lines: po.lines.map((l) => ({ id: l.id, sku: l.sku, title: l.title, remaining: l.remaining })),
          }}
          onClose={() => setReceiving(false)}
        />
      )}
    </PageFrame>
  );
}

function EditDialog({ po, onClose }: { po: PurchaseOrderDetail; onClose: () => void }) {
  const t = useT();
  const save = useApiMutation(
    (body: Record<string, unknown>) =>
      api.patch(`/v1/admin/purchase-orders/${po.id}`, body, { ifMatch: po.version }),
    {
      invalidate: [['purchase-order', po.id], ['purchase-orders']],
      success: t('admin.purchasing.orderSaved'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t('admin.purchasing.editLines')}
      closeLabel={t('common.close')}
      variant="sheet"
    >
      <PoForm
        lockHeader
        initial={{
          supplierId: po.supplierId,
          warehouseId: po.warehouseId,
          currency: po.currency,
          expectedDate: po.expectedDate ?? '',
          note: po.note ?? '',
          lines: po.lines.map((l) => ({
            variantId: l.variantId,
            label: `${l.title} (${l.sku})`,
            quantity: String(l.quantity),
            unitCost: minorToInput(l.unitCostMinor),
            tax: String(l.taxRateBps / 100).replace('.', ','),
          })),
        }}
        submitLabel={t('admin.purchasing.saveOrder')}
        pending={save.isPending}
        error={save.error}
        onSubmit={(_, body) => save.mutate(body)}
      />
    </Dialog>
  );
}

function DateDialog({ po, onClose }: { po: PurchaseOrderDetail; onClose: () => void }) {
  const t = useT();
  const [date, setDate] = useState(po.expectedDate ?? '');
  const save = useApiMutation(
    () =>
      api.patch(
        `/v1/admin/purchase-orders/${po.id}`,
        { expectedDate: date || null },
        { ifMatch: po.version },
      ),
    {
      invalidate: [['purchase-order', po.id], ['purchase-orders'], ['receivable']],
      success: t('admin.purchasing.orderSaved'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t('admin.purchasing.changeDate')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(undefined)}>
            {t('admin.purchasing.saveDate')}
          </Button>
        </>
      }
    >
      <Field label={t('admin.purchasing.expectedDate')} error={fieldError(save.error, 'expectedDate')}>
        {(p) => <Input {...p} type="date" value={date} onChange={(e) => setDate(e.target.value)} />}
      </Field>
    </Dialog>
  );
}
